import React, {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react";
import {
  Control,
  FieldValues,
  Message,
  MultipleFieldErrors,
  Ref,
  SubmitHandler,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
  UseFormWatch,
  ValidationMode,
} from "react-hook-form";
import { AnyObjectSchema } from "yup";

export type FormPreProps = {
  register: UseFormRegister<FieldValues>;
  isSubmitting: boolean;
  isSubmitted?: boolean;
  errors: { [error: string]: FieldError | undefined };
  control?: Control;
  defaultValues?: FieldValues;
  reset?: UseFormReset<FieldValues>;
  watch?: UseFormWatch<FieldValues>;
  setValue?: UseFormSetValue<FieldValues>;
};
export type FormProps = {
  // Function component to display in the form
  renderForm: (formProps: FormPreProps) => React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmitForm: SubmitHandler<any>;
  schema?: AnyObjectSchema;
  defaultValues?: FieldValues;
  onWatch?: (values: FieldValues) => void;
  mode?: keyof ValidationMode;
};
export type SelectValue = {
  label: string;
  value: string | number | object;
};

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  name: string;
  type?: string;
  label?: string | React.ReactElement;
  register?: UseFormRegister<FieldValues>;
  control?: Control;
  error?: string;
  wrapperClass?: string;
  className?: string;
  values: SelectValue[] | undefined;
}
export type InputType = "text" | "tel" | "email" | "number" | "password";
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  type?: InputType | string;
  label?: string | React.ReactElement;
  register?: UseFormRegister<FieldValues>;
  control?: Control;
  error?: string;
  wrapperClass?: string;
  className?: string;
  readonly?: boolean;
  dataTest?: string;
}
export type FieldError = {
  type?: string;
  ref?: Ref;
  types?: MultipleFieldErrors;
  message?: Message;
};
export interface FieldPropsType<T, S> {
  register?: UseFormRegister<FieldValues>;
  control?: Control;
  name: string;
  type?: InputType | string;
  error?: T;
  label?: string | S;
  placeholder?: string;
  required?: boolean;
  wrapperClass?: string;
  values?: SelectValue[] | undefined;
  options?: SelectValue[];
  class?: string;
  className?: string;
  readonly?: boolean;
  marks?: Record<string | number, ReactNode>;
  min?: number;
  defaultValue?: string | number;
  startLabel?: string;
  endLabel?: string;
  setValue?: UseFormSetValue<FieldValues>;
  fieldsToComplete?: IFieldsToComplete[];
  countryCode?: string;
  availableCountries?: string[];
  dataTest?: string;
  disabled?: boolean;
}
export interface FieldProps {
  variant: string;
  props: FieldPropsType<string, React.ReactElement>;
}
export type TFunctionFieldProps = {
  field: FieldPropsType<{ [error: string]: string | number }, string>;
  register?: UseFormRegister<FieldValues>;
  errors: { [error: string]: FieldError | undefined };
  control?: Control;
};
export interface IOption extends SelectValue {
  disabled?: boolean;
}
export interface IRadioGroup extends InputProps {
  values: SelectValue[] | undefined;
  hasFullWidth?: boolean;
}
export interface RadioElementProps
  extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  name: string;
  label: string | React.ReactElement;
  value: string;
  disabled?: boolean;
  className?: string;
}

export interface ISlider extends InputProps {
  min?: number;
  marks?: Record<string | number, React.ReactNode> | undefined;
  defaultVallue?: number | string;
  startLabel?: string;
  endLabel?: string;
}

export interface IFieldsToComplete {
  name: string;
  type: string;
}

export interface IErrors {
  [error: string]: FieldError | undefined;
}

export enum EAddressFieldTypes {
  POSTCODE = "postcode",
  CITY = "city",
  STREET = "street",
}

export enum EFieldTypes {
  TEXT = "text",
  EMAIL = "email",
  TEL = "tel",
  NUMBER = "number",
  PASSWORD = "password",
  SELECT = "select",
  RADIO = "radio",
  CHECKBOX = "checkbox",
  SLIDER = "slider",
  AUTOCOMPLETE_ADDRESS = "autocompleteAddress",
}
